import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AdminSidebar } from './AdminSidebar';
import { Header } from './Header';
import { useToast } from '../common/Toast';
import { useAuthStore } from '../../store/authStore';
import { cn } from '../../utils/cn';
import { useLanguage } from '../../features/language/useLanguage';
import { ForbiddenState } from '../common/ForbiddenState';
import { authService } from '../../services/authService';

const normalizeRole = (role: string | null | undefined): string =>
  (role ?? '').trim().toLowerCase().replace(/\s+/g, '_');

export const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { t } = useLanguage();
  const logout = useAuthStore((state) => state.logout);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const userRole = useAuthStore((state) => state.user?.role);
  const syncProfile = useAuthStore((state) => state.syncProfile);

  useEffect(() => {
    const handleUnauthorized = () => {
      showToast(t('dashboard.sessionExpired'), 'error');
      logout();
      navigate('/login');
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, [navigate, showToast, logout, t]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let isCancelled = false;

    const syncAuthProfile = async () => {
      try {
        const response = await authService.getMe();
        const backendUser = response?.user;
        const backendCompany = response?.company;
        if (isCancelled || !backendUser || !backendCompany) {
          return;
        }

        syncProfile(
          {
            id: backendUser.id,
            email: backendUser.email,
            name: backendUser.full_name,
            role: backendUser.role,
            companyId: backendUser.company_id,
            emailNotificationsEnabled: backendUser.email_notifications_enabled ?? false,
          },
          {
            id: backendCompany.id,
            name: backendCompany.name,
            code: backendCompany.code,
            address: backendCompany.address,
            email: backendCompany.email,
            phone: backendCompany.phone,
            website: backendCompany.website,
            currency_symbol: backendCompany.currency_symbol,
          },
          response?.permissions,
          response?.company_can_edit_roles,
        );
      } catch {
        if (!isCancelled) {
          logout();
          navigate('/login', { replace: true });
        }
      }
    };

    void syncAuthProfile();
    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, logout, navigate, syncProfile]);

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const isSuperAdmin = normalizeRole(userRole) === 'super_admin';
  if (!isSuperAdmin) {
    return <ForbiddenState variant="page" />;
  }

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden" data-test-id="admin-layout">
      <AdminSidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div
        className={cn(
          'flex-1 flex flex-col overflow-hidden min-h-0 min-w-0 transition-all duration-300',
          isSidebarCollapsed ? 'md:ml-20' : 'md:ml-64',
        )}
      >
        <Header onMenuClick={() => setSidebarOpen(true)} />
        <main className="flex-1 overflow-y-auto overflow-x-hidden min-h-0 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
